import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Upload, ImagePlus, Check, MapPin, Loader2, Cloud } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { parse } from "exifr";
import type { TravelPhoto } from "@/data/photos";

/**
 * 上传图片到 Cloudinary
 */
async function uploadToCloudinary(file: File): Promise<{ url: string; public_id: string } | null> {
  // 从 localStorage 获取配置
  const cloudName = localStorage.getItem("cloudinary_cloud_name") || "";
  const uploadPreset = localStorage.getItem("cloudinary_upload_preset") || "";
  
  if (!cloudName) {
    toast.error("请先配置 Cloudinary Cloud Name");
    return null;
  }

  if (!uploadPreset) {
    toast.error("请先配置 Cloudinary Upload Preset");
    return null;
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);
  formData.append("folder", "travel-photos");

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      const error = await response.json();
      console.error("Cloudinary 错误详情:", error);
      throw new Error(error.error?.message || error.message || "上传失败");
    }

    const data = await response.json();
    return {
      url: data.secure_url,
      public_id: data.public_id,
    };
  } catch (error) {
    console.error("Cloudinary 上传失败:", error);
    toast.error(`上传到云端失败: ${(error as Error).message}`);
    return null;
  }
}

/**
 * 将文件转换为压缩后的 base64 缩略图
 */
function fileToCompressedBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_SIZE = 400;
        let width = img.width;
        let height = img.height;
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * 解析 GPS 坐标
 */
function parseGpsCoord(value: any, ref?: string): number | null {
  if (value == null) return null;
  if (typeof value === "number") {
    let coord = value;
    if (ref === "S" || ref === "W") coord = -coord;
    return coord;
  }
  if (typeof value === "string") {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      let coord = num;
      if (ref === "S" || ref === "W") coord = -coord;
      return coord;
    }
  }
  if (Array.isArray(value)) {
    const [deg, min, sec] = value;
    if (deg == null || min == null) return null;
    let decimal =
      Math.abs(Number(deg)) +
      Number(min) / 60 +
      (sec != null ? Number(sec) / 3600 : 0);
    if (ref === "S" || ref === "W") decimal = -decimal;
    return decimal;
  }
  return null;
}

/**
 * 从 EXIF 日期字符串解析 Date 对象
 */
function parseExifDate(exifDate: any): Date | null {
  if (!exifDate) return null;
  try {
    const str = exifDate.toString().trim();
    
    if (str.includes(" ")) {
      const [datePart] = str.split(" ");
      const separators = [":", "/", "-"];
      for (const sep of separators) {
        if (datePart.includes(sep)) {
          const parts = datePart.split(sep).map(Number);
          if (parts.length >= 3 && parts[0] > 1900 && parts[0] < 2100) {
            return new Date(parts[0], parts[1] - 1, parts[2]);
          }
        }
      }
    }
    
    const d = new Date(str);
    if (!isNaN(d.getTime())) return d;
  } catch { /* ignore */ }
  return null;
}

interface ParsedPhoto {
  preview: string;
  photo: TravelPhoto | null;
  status: "parsing" | "uploading" | "success" | "no-gps" | "error";
  fileName: string;
}

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onPhotosParsed?: (photos: TravelPhoto[]) => void;
}

export default function UploadModal({ open, onClose, onPhotosParsed }: UploadModalProps) {
  const [items, setItems] = useState<ParsedPhoto[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [cloudName, setCloudName] = useState(localStorage.getItem("cloudinary_cloud_name") || "");
  const [uploadPreset, setUploadPreset] = useState(localStorage.getItem("cloudinary_upload_preset") || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<ParsedPhoto[]>([]);
  const updateBatchRef = useRef(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const saveConfig = () => {
    localStorage.setItem("cloudinary_cloud_name", cloudName);
    localStorage.setItem("cloudinary_upload_preset", uploadPreset);
    toast.success("Cloudinary 配置已保存");
    setShowConfig(false);
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (fileArray.length === 0) return;

    submittedRef.current = false;

    const initialItems: ParsedPhoto[] = fileArray.map((file) => ({
      preview: URL.createObjectURL(file),
      photo: null,
      status: "parsing",
      fileName: file.name,
    }));
    
    itemsRef.current = initialItems;
    setItems(initialItems);
    updateBatchRef.current = 0;

    const BATCH_SIZE = 3;
    const parsedPhotos: TravelPhoto[] = [];

    for (let batchStart = 0; batchStart < fileArray.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, fileArray.length);
      const batch = fileArray.slice(batchStart, batchEnd);

      for (let j = 0; j < batch.length; j++) {
        const file = batch[j];
        const idx = batchStart + j;

        try {
          const exif = await parse(file, { gps: true });

          const lat = parseGpsCoord(exif?.latitude, exif?.GPSLatitudeRef) ??
                      parseGpsCoord(exif?.GPSLatitude, exif?.GPSLatitudeRef);
          const lng = parseGpsCoord(exif?.longitude, exif?.GPSLongitudeRef) ??
                      parseGpsCoord(exif?.GPSLongitude, exif?.GPSLongitudeRef);

          if (lat != null && lng != null) {
            // 更新状态为上传中
            itemsRef.current[idx] = { ...itemsRef.current[idx], status: "uploading" };
            setItems([...itemsRef.current]);
            
            // 上传到 Cloudinary
            const cloudinaryResult = await uploadToCloudinary(file);
            
            // 使用 Cloudinary URL 或本地 base64
            let thumbnail: string;
            if (cloudinaryResult) {
              thumbnail = cloudinaryResult.url;
            } else {
              thumbnail = await fileToCompressedBase64(file);
            }
            
            let photoDate = new Date().toISOString().split("T")[0];
            let photoYear = new Date().getFullYear();
            
            const parsedDate = parseExifDate(exif?.DateTimeOriginal) 
              || parseExifDate(exif?.CreateDate) 
              || parseExifDate(exif?.ModifyDate)
              || parseExifDate(exif?.DateTime);
            
            if (parsedDate) {
              photoDate = parsedDate.toISOString().split("T")[0];
              photoYear = parsedDate.getFullYear();
            }
            
            const newPhoto: TravelPhoto = {
              id: Date.now() + idx,
              lat,
              lng,
              date: photoDate,
              year: photoYear,
              thumbnail,
              title: file.name.replace(/\.[^/.]+$/, ""),
              mood: " newly added ",
              city: "未知地点",
              country: "中国",
            };

            parsedPhotos.push(newPhoto);
            itemsRef.current[idx] = { ...itemsRef.current[idx], photo: newPhoto, status: "success" };
          } else {
            itemsRef.current[idx] = { ...itemsRef.current[idx], status: "no-gps" };
          }
        } catch (err) {
          toast.error(`解析 ${file.name} 失败: ${(err as Error).message}`);
          itemsRef.current[idx] = { ...itemsRef.current[idx], status: "error" };
        }
      }

      updateBatchRef.current++;
      setItems([...itemsRef.current]);
      await new Promise((r) => setTimeout(r, 50));
    }

    // 保存到 localStorage
    if (parsedPhotos.length > 0) {
      try {
        const existing = localStorage.getItem("customPhotos");
        const existingPhotos: TravelPhoto[] = existing ? JSON.parse(existing) : [];
        const allPhotos = [...existingPhotos, ...parsedPhotos];
        localStorage.setItem("customPhotos", JSON.stringify(allPhotos));
        toast.success(`已保存 ${parsedPhotos.length} 张照片`);
      } catch (err) {
        console.error("保存失败:", err);
      }
    }

    // 处理完成后，自动提交结果
    if (parsedPhotos.length > 0 && !submittedRef.current) {
      submittedRef.current = true;
      onPhotosParsed?.(parsedPhotos);
      setTimeout(() => {
        itemsRef.current = [];
        setItems([]);
        onClose();
      }, 800);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  const handleClose = () => {
    itemsRef.current = [];
    setItems([]);
    submittedRef.current = false;
    onClose();
  };

  const successCount = items.filter((i) => i.status === "success").length;
  const isProcessing = items.some((i) => i.status === "parsing" || i.status === "uploading");

  const modalContent = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{
            backgroundColor: "oklch(0.18 0.06 45 / 0.55)",
            zIndex: 99999,
          }}
          onClick={!isProcessing ? handleClose : undefined}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="w-full max-w-lg rounded-2xl overflow-hidden max-h-[85vh] flex flex-col"
            style={{
              backgroundColor: "oklch(0.99 0.01 70)",
              boxShadow: "var(--ds-shadow-xl)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between flex-shrink-0"
              style={{
                padding: "var(--spacing-md) var(--spacing-lg)",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div className="flex items-center" style={{ gap: "var(--spacing-sm)" }}>
                <ImagePlus className="w-5 h-5" style={{ color: "oklch(0.62 0.18 35)" }} />
                <h3
                  className="font-semibold"
                  style={{
                    fontSize: "var(--font-size-title)",
                    color: "var(--foreground)",
                  }}
                >
                  上传照片
                </h3>
              </div>
              <div className="flex items-center" style={{ gap: "var(--spacing-sm)" }}>
                <button
                  onClick={() => setShowConfig(!showConfig)}
                  className="p-1.5 rounded-full hover:bg-muted transition-colors"
                  title="Cloudinary 配置"
                >
                  <Cloud className="w-5 h-5" style={{ color: "oklch(0.5 0.1 240)" }} />
                </button>
                {!isProcessing && (
                  <button
                    onClick={handleClose}
                    className="p-1.5 rounded-full hover:bg-muted transition-colors"
                  >
                    <X className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
                  </button>
                )}
              </div>
            </div>

            {/* Cloudinary Config */}
            <AnimatePresence>
              {showConfig && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <div className="p-4 space-y-3">
                    <p className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
                      Cloudinary 配置（可选）
                    </p>
                    <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                      配置后照片将上传到云端，所有人都能看到
                    </p>
                    <input
                      type="text"
                      value={cloudName}
                      onChange={(e) => setCloudName(e.target.value)}
                      placeholder="Cloud Name（从 Cloudinary Dashboard 获取）"
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      style={{ borderColor: "var(--border)" }}
                    />
                    <input
                      type="text"
                      value={uploadPreset}
                      onChange={(e) => setUploadPreset(e.target.value)}
                      placeholder="Upload Preset（在 Cloudinary 设置中创建）"
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                      style={{ borderColor: "var(--border)" }}
                    />
                    <p className="text-xs" style={{ color: "oklch(0.65 0.18 25)" }}>
                      提示：Upload Preset 必须在 Cloudinary 控制台中预先创建，并开启「Unsigned」选项
                    </p>
                    <button
                      onClick={saveConfig}
                      className="w-full py-2 rounded-lg text-sm font-medium"
                      style={{
                        backgroundColor: "oklch(0.5 0.1 240 / 0.1)",
                        color: "oklch(0.45 0.15 240)",
                        border: "1px solid oklch(0.5 0.1 240 / 0.2)",
                      }}
                    >
                      保存配置
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              <div className="flex flex-col" style={{ padding: "var(--spacing-lg)", gap: "var(--spacing-md)" }}>
                {/* Upload area */}
                <div
                  onClick={() => !isProcessing && inputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed cursor-pointer transition-all"
                  style={{
                    padding: "var(--spacing-2xl)",
                    gap: "var(--spacing-sm)",
                    borderColor: dragOver ? "oklch(0.62 0.18 35)" : "var(--border)",
                    backgroundColor: dragOver ? "oklch(0.62 0.18 35 / 0.05)" : "oklch(0.97 0.015 70)",
                    opacity: isProcessing ? 0.6 : 1,
                    pointerEvents: isProcessing ? "none" : "auto",
                  }}
                >
                  <Upload className="w-8 h-8" style={{ color: "oklch(0.62 0.18 35)" }} />
                  <p style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)" }}>
                    {isProcessing ? "正在处理..." : "点击或拖拽照片到此处"}
                  </p>
                  <p style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                    自动提取 GPS 位置信息并添加到地图
                  </p>
                  {cloudName && uploadPreset && (
                    <p style={{ fontSize: "var(--font-size-small)", color: "oklch(0.5 0.1 240)" }}>
                      ✓ 已配置 Cloudinary，照片将上传到云端
                    </p>
                  )}
                  {cloudName && !uploadPreset && (
                    <p style={{ fontSize: "var(--font-size-small)", color: "oklch(0.65 0.18 25)" }}>
                      ⚠ 缺少 Upload Preset，请在配置中添加
                    </p>
                  )}
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => processFiles(e.target.files)}
                />

                {/* Photo list */}
                {items.length > 0 && (
                  <div className="flex flex-col" style={{ gap: "var(--spacing-sm)" }}>
                    <p style={{ fontSize: "var(--font-size-label)", color: "var(--muted-foreground)", fontWeight: 500 }}>
                      已解析 {items.length} 张照片 · {successCount} 张成功
                    </p>

                    <div className="flex flex-col" style={{ gap: "var(--spacing-xs)" }}>
                      {items.slice(0, 50).map((item, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="flex items-center rounded-lg"
                          style={{
                            gap: "var(--spacing-xs)",
                            padding: "var(--spacing-xs) var(--spacing-sm)",
                            backgroundColor: "oklch(0.96 0.02 65)",
                          }}
                        >
                          <img
                            src={item.preview}
                            alt={item.fileName}
                            className="rounded-md object-cover flex-shrink-0"
                            style={{ width: 40, height: 40 }}
                          />
                          <span
                            className="flex-1 truncate"
                            style={{ fontSize: "var(--font-size-small)", color: "var(--foreground)" }}
                          >
                            {item.fileName}
                          </span>

                          {item.status === "parsing" && (
                            <Loader2 className="w-4 h-4 animate-spin" style={{ color: "oklch(0.62 0.18 35)" }} />
                          )}
                          {item.status === "uploading" && (
                            <span className="flex items-center" style={{ gap: "var(--spacing-xs)" }}>
                              <Cloud className="w-3 h-3" style={{ color: "oklch(0.5 0.1 240)" }} />
                              <span style={{ fontSize: "var(--font-size-small)", color: "oklch(0.5 0.1 240)" }}>
                                上传中
                              </span>
                            </span>
                          )}
                          {item.status === "success" && item.photo && (
                            <span className="flex items-center" style={{ gap: "var(--spacing-xs)" }}>
                              <MapPin className="w-3 h-3" style={{ color: "oklch(0.62 0.18 35)" }} />
                              <Check className="w-4 h-4" style={{ color: "oklch(0.65 0.14 160)" }} />
                            </span>
                          )}
                          {item.status === "no-gps" && (
                            <span style={{ fontSize: "var(--font-size-small)", color: "var(--muted-foreground)" }}>
                              无 GPS
                            </span>
                          )}
                          {item.status === "error" && (
                            <span style={{ fontSize: "var(--font-size-small)", color: "oklch(0.65 0.18 25)" }}>
                              失败
                            </span>
                          )}
                        </motion.div>
                      ))}
                      {items.length > 50 && (
                        <div className="text-center py-2" style={{ color: "var(--muted-foreground)", fontSize: "var(--font-size-label)" }}>
                          ... 还有 {items.length - 50} 张照片
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (!mounted) return null;

  return createPortal(modalContent, document.body);
}